import { atom } from "jotai";
import {
    allUsersAtom,
    allSchedulesAtom,
    MIN_SEARCH_TERM_LENGTH
} from "./atoms";

import moment from "moment";
import _ from "lodash";
export const ROLE_OPTIONS = {
    ALL: "all",
    ADMINS: "admins",
    MANAGERS: "managers",
    PROVIDERS: "providers"
};
export const STATUS_OPTIONS = {
    ALL: "all",
    ACTIVE: "active",
    INACTIVE: "inactive"
};
export const AVAILABILITY_OPTIONS = {
    ALL: "all",
    ACTIVE_SCHEDULE: "hasActiveSchedule",
    NO_ACTIVE_SCHEDULE: "noActiveSchedule"
};

const PHONE_PREF_MOBILE = "MOBILE";
const PHONE_PREF_WORK = "WORK";
const PHONE_PREF_HOME = "HOME";

const tableConfigBaseAtom = atom({
    searchTerm: "",
    statusFilter: STATUS_OPTIONS.ACTIVE,
    roleFilter: ROLE_OPTIONS.ALL,
    availabilityFilter: AVAILABILITY_OPTIONS.ALL,
    sort: { sortBy: null, direction: null }
});

export const tableConfigAtom = atom(
    (get) => get(tableConfigBaseAtom),
    (_get, set, newTableConfig) => {
        set(tableConfigBaseAtom, newTableConfig);
        set(setPageAtom, 0);
    }
);

export const paginationAtom = atom({ page: 0, perPage: 25 });
export const setPageAtom = atom(null, (_get, set, page) => {
    set(paginationAtom, (prev) => ({ ...prev, page }));
});

export const setPerPageAtom = atom(null, (_get, set, perPage) => {
    set(paginationAtom, (prev) => {
        const newPageVal =
            prev.page * perPage > _get(visibleRows).length ? 0 : prev.page;
        return { ...prev, perPage, page: newPageVal };
    });
});
export const userModalTabValue = atom(0);
export const selectedUserIdAtom = atom(null);
export const selectedAvailabilityScIdAtom = atom(null);
export const openAvailabilityDialogAtom = atom(false);
export const selectedUserProviderLocationsAtom = atom([]);
export const updateSelectedUserProviderLocationsAtom = atom(
    null,
    (get, set, updatedProviderLocation) => {
        const currentProviderLocations = get(selectedUserProviderLocationsAtom);

        const existingIndex = currentProviderLocations.findIndex(
            (location) => location.id === updatedProviderLocation.id
        );

        let updatedProviderLocations;
        if (existingIndex !== -1) {
            updatedProviderLocations = [...currentProviderLocations];
            updatedProviderLocations[existingIndex] = updatedProviderLocation;
        } else {
            updatedProviderLocations = [
                ...currentProviderLocations,
                updatedProviderLocation
            ];
        }

        set(selectedUserProviderLocationsAtom, updatedProviderLocations);
    }
);
export const selectedUserServicesAtom = atom((get) =>
    _.map(get(selectedProviderAtom)?.servicetypes?.items, "servicetype")
);
export const openUserDialogAtom = atom(false);
export const userPageSnackbarAtom = atom({
    open: false,
    message: "",
    severity: "success"
});

export const formattedUsersAtom = atom([]);
export const formatUsersAtom = atom((get) => {
    const blocks = get(allSchedulesAtom);
    const users = get(allUsersAtom);
    const usersWithAvail = users?.map((user) => ({
        ...user,
        ...{
            availability: blocks?.filter(
                ({ providerId, scheduleinfo, endDate }) =>
                    providerId === user.providerId &&
                    scheduleinfo.startsWith("SB") &&
                    new Date(endDate) > new Date()
            )
        }
    }));
    return formatUsersHelper(usersWithAvail);
});
const formatUsersHelper = (payload) => {
    return payload.map(
        ({
            id,
            emailaddress: email,
            firstname: firstName,
            lastname: lastName,
            availability = [],
            provider,
            mobilephone = "",
            workphone = "",
            homephone = "",
            phonepref,
            active,
            role,
            LoginInfo,
            createdAt
        }) => {
            const {
                skills: skillsFromApi,
                servicetypes,
                pictures3key,
                permalink,
                active: providerActive,
                locations: { items: providerLocations = [] } = {}
            } = provider || {};
            const phones = {
                [PHONE_PREF_MOBILE]: mobilephone,
                [PHONE_PREF_WORK]: workphone,
                [PHONE_PREF_HOME]: homephone
            };
            const locationLabel = (availabilityLocation) => {
                const locationIdentifier = availabilityLocation.slice(0, 2);
                const locationId = availabilityLocation.slice(
                    3,
                    availabilityLocation.length
                );
                let label = "";
                switch (locationIdentifier) {
                    case "GL":
                        label = "Travel Zone";
                        break;
                    case "PL":
                        label =
                            providerLocations.find(
                                ({ id }) => id === locationId
                            )?.name || "Provider Location";
                        break;
                    case "CL":
                        // TODO: Change to location name when company data is in atomic state
                        label = "Company Location";
                        break;
                    default:
                        break;
                }
                return label;
            };
            const { items: skills = [] } = skillsFromApi || {}; // because of null
            const { items: services = [] } = servicetypes || {}; // because of null
            let activeSchedule = availability.length;
            let lastLoginDate = "";
            try {
                lastLoginDate = JSON.parse(LoginInfo)?.lastLoginDate;
            } catch (e) {
                console.log(e);
            }
            return {
                id,
                email,
                firstName,
                lastName,
                availability,
                skills: skills.filter(({ skill }) => {
                    // Only filter out if 'deleted' and 'active' are defined
                    // since when we first save the form in profile tab, we can only pass the id as the value and not the whole service obj.
                    return skill.deleted !== true && skill.active !== false;
                }),
                services: services.filter(({ servicetype }) => {
                    // Only filter out if 'deleted' and 'active' are defined
                    // since when we first save the form in profile tab, we can only pass the id as the value and not the whole service obj.
                    return (
                        servicetype.deleted !== true &&
                        servicetype.active !== false
                    );
                }),
                permalink,
                activeSchedule,
                phone: phones[phonepref],
                role,
                active,
                providerActive,
                lastLoginDate,
                createdAt,
                photoUrl: pictures3key
            };
        }
    );
};

export const visibleRows = atom((get) => {
    const {
        searchTerm,
        roleFilter,
        statusFilter,
        availabilityFilter,
        sort: { sortBy, direction }
    } = get(tableConfigAtom);

    // Filtering logic
    let filtered = [];
    const allFormattedUsers = get(formatUsersAtom);
    filtered =
        searchTerm && searchTerm.length > MIN_SEARCH_TERM_LENGTH
            ? allFormattedUsers.filter(
                  ({ firstName = "", lastName = "", email = "" }) => {
                      const lowerCaseSearchTerm = searchTerm.toLowerCase();
                      const searchTerms = lowerCaseSearchTerm.split(" ");

                      return searchTerms.every(
                          (term) =>
                              firstName.toLowerCase().includes(term) ||
                              lastName.toLowerCase().includes(term) ||
                              email.toLowerCase().includes(term)
                      );
                  }
              )
            : allFormattedUsers;

    filtered =
        filtered.length && roleFilter !== ROLE_OPTIONS.ALL
            ? filtered.filter(({ role }) =>
                  roleFilter === ROLE_OPTIONS.ADMINS
                      ? role === "COMPANY_ADMIN_PROVIDER"
                      : roleFilter === ROLE_OPTIONS.MANAGERS
                        ? role === "MANAGER_PROVIDER"
                        : role === "PROVIDER"
              )
            : filtered;

    filtered =
        filtered.length && statusFilter !== STATUS_OPTIONS.ALL
            ? filtered.filter(({ active }) =>
                  statusFilter === "active" ? active : !active
              )
            : filtered;

    filtered =
        filtered.length && availabilityFilter !== AVAILABILITY_OPTIONS.ALL
            ? filtered.filter(({ activeSchedule }) =>
                  availabilityFilter === AVAILABILITY_OPTIONS.ACTIVE_SCHEDULE
                      ? activeSchedule
                      : !activeSchedule
              )
            : filtered;

    // Sorting logic
    let sorted = [];
    switch (sortBy) {
        // All the columns that can be directly compared
        case "role":
        case "active": // type corrosion is bad but so convenient (true > false === true)
        case "services":
        case "skills":
            sorted = filtered.sort((a, b) =>
                direction === "ASC"
                    ? a[sortBy] > b[sortBy]
                        ? 1
                        : -1
                    : a[sortBy] < b[sortBy]
                      ? 1
                      : -1
            );
            break;

        // Date comparison columns
        case "createdAt":
        case "lastLoginDate":
            sorted = filtered.sort((a, b) => {
                const dateA = a[sortBy] ? moment(a[sortBy]) : null;
                const dateB = b[sortBy] ? moment(b[sortBy]) : null;

                //handle cases where one or both dates are missing
                if (!dateA && !dateB) return 0;
                if (!dateA) return direction === "ASC" ? -1 : 1;
                if (!dateB) return direction === "ASC" ? 1 : -1;

                const dateDiff = dateA.diff(dateB);
                return direction === "ASC" ? dateDiff : -dateDiff;
            });
            break;

        case "user":
            sorted = filtered.sort((a, b) => {
                const fullNameA = `${a.firstName} ${a.lastName}`;
                const fullNameB = `${b.firstName} ${b.lastName}`;
                return direction === "ASC"
                    ? fullNameA.localeCompare(fullNameB)
                    : fullNameB.localeCompare(fullNameA);
            });
            break;

        case "availability":
            sorted = filtered.sort((a, b) => {
                // Use getScheduleCount to calculate the schedule count
                const getScheduleCount = (availability) => {
                    const schedules = new Set(
                        availability.map((item) => {
                            const schedulePart =
                                item.scheduleinfo.split("|")[2]; // Extract schedule part
                            return schedulePart;
                        })
                    );
                    return schedules.size;
                };

                const scheduleCountA = getScheduleCount(a.availability || []);
                const scheduleCountB = getScheduleCount(b.availability || []);

                return direction === "ASC"
                    ? scheduleCountA - scheduleCountB
                    : scheduleCountB - scheduleCountA;
            });
            break;

        default:
            // Default sorting: last name ASC, then first name ASC, then createdAt ASC
            sorted = filtered.sort((a, b) => {
                const firstNameA = a.firstName.toLowerCase();
                const firstNameB = b.firstName.toLowerCase();
                const lastNameA = a.lastName.toLowerCase();
                const lastNameB = b.lastName.toLowerCase();
                const createdAtA = moment(a.createdAt);
                const createdAtB = moment(b.createdAt);

                // Sort by first name
                if (firstNameA < firstNameB) return -1; // ASC
                if (firstNameA > firstNameB) return 1; // ASC

                // Sort by last name if first names are equal
                if (lastNameA < lastNameB) return -1; // ASC
                if (lastNameA > lastNameB) return 1; // ASC

                // Finally, sort by created date if first names are also equal
                return createdAtA.isBefore(createdAtB) ? -1 : 1; // ASC
            });
    }
    return sorted;
});

export const visibleRowsPaginated = atom((get) => {
    const { page, perPage } = get(paginationAtom);
    const visible = get(visibleRows);
    return visible.slice(perPage * page, perPage * (page + 1));
});
export const selectedUserAtom = atom((get) => {
    const allUsers = get(allUsersAtom);
    const selectedUserId = get(selectedUserIdAtom);
    if (selectedUserId === null) {
        return null;
    }

    const blocks = get(allSchedulesAtom);
    let user = allUsers.find((user) => user.id === selectedUserId);
    if (!user) return null; //if the user was not found - then the selectedUser should be null
    user = {
        ...user,
        ...{
            availability: blocks?.filter(
                ({ providerId, scheduleinfo }) =>
                    providerId === user.providerId &&
                    scheduleinfo.startsWith("SB")
            )
        }
    };
    return user;
});
export const selectedProviderAtom = atom(
    (get) => get(selectedUserAtom)?.provider
);
export const selectedProviderIdAtom = atom(
    (get) => get(selectedUserAtom)?.providerId
);

export const selectedProviderSchedulesAtom = atom([]);

export const updateSelectedProviderSchedulesAtom = atom(
    null,
    (get, set, oldRecords, newRecords) => {
        const currentSchedules = get(selectedProviderSchedulesAtom);
        const updatedSchedules = currentSchedules.filter(
            (record) =>
                !oldRecords.some(
                    (oldRecord) =>
                        record.id === oldRecord.id &&
                        record.scheduleinfo.includes(oldRecord.scheduleinfo)
                )
        );
        const finalSchedules = [...updatedSchedules, ...newRecords];
        set(selectedProviderSchedulesAtom, finalSchedules);
    }
);

export const selectedUserSchedulesAtom = atom((get) => {
    const allUsersSchedules = get(selectedProviderSchedulesAtom);
    const selectedUser = get(selectedUserAtom);

    if (!selectedUser) {
        return null;
    }

    const userSchedules = allUsersSchedules.filter(
        (schedule) => schedule.providerId === selectedUser?.provider?.id
    );
    //Filter SC records and initialize them with empty arrays for SB, SL, SR
    const scheduleMap = userSchedules
        .filter((schedule) => schedule.scheduleinfo.startsWith("SC|"))
        .reduce((acc, schedule) => {
            acc[schedule.scheduleinfo] = {
                ...schedule,
                SB: [],
                SL: [],
                SR: []
            };
            return acc;
        }, {});

    //Attach SB, SL, SR records to their corresponding SC records
    userSchedules.forEach((schedule) => {
        const { scheduleinfo } = schedule;
        const parts = scheduleinfo.split("|");
        const type = parts[0];
        const scIndex = parts.indexOf("SC");
        if (scIndex !== -1) {
            const scId = `SC|${parts[scIndex + 1]}`;
            if (["SB", "SL", "SR"].includes(type)) {
                if (scheduleMap[scId]) {
                    scheduleMap[scId][type].push(schedule);
                }
            }
        }
    });

    //Convert the map values to an array of SC records with their associated SB, SL, SR records
    const organizedSchedules = Object.values(scheduleMap);
    return organizedSchedules;
});

export const selectedScheduleAtom = atom((get) => {
    const selectedUserSchedules = get(selectedUserSchedulesAtom);
    const selectedAvailabilityScId = get(selectedAvailabilityScIdAtom);
    const selectedProviderLocations = get(selectedUserProviderLocationsAtom);

    if (!selectedUserSchedules || !selectedAvailabilityScId) {
        return null;
    }
    let selectedSchedule =
        selectedUserSchedules.find(
            (schedule) => schedule.scheduleinfo === selectedAvailabilityScId
        ) || null;

    if (selectedSchedule && selectedProviderLocations) {
        const scheduleTravelZone = selectedProviderLocations.find(
            (providerLocation) => {
                const providerLocationId = providerLocation.id;
                const scheduleLocations = JSON.parse(
                    selectedSchedule.locations
                );

                return scheduleLocations.find(
                    (record) => record === `PL-${providerLocationId}`
                );
            }
        );
        selectedSchedule = {
            ...selectedSchedule,
            travelZone: scheduleTravelZone
        };
    }
    return selectedSchedule;
});

export const googleCalRefetchIntervalAtom = atom(false);
export const googleIntegrationCalInfoAtom = atom(null);

export const updateGoogleIntegrationCalInfoAtom = atom(
    null,
    (get, set, newValue) => {
        const userId = get(selectedUserIdAtom);
        if (userId) {
            set(googleIntegrationCalInfoAtom, newValue);
        }
    }
);
